/* eslint-disable no-restricted-globals -- direct sessionStorage use, wrapped in try/catch. */
import * as React from 'react'

import {
  AIDA_LAUNCHER_EDGE_KEY,
  AIDA_LAUNCHER_PEEK_RATIO_KEY,
  LEGACY_AIDA_LAUNCHER_EDGE_KEY,
  LEGACY_AIDA_LAUNCHER_PEEK_RATIO_KEY,
} from '../utils/constants/storage-keys'

/** Matches Tailwind `lg` — keep in sync with `size-10 lg:size-16` on the button. */
export const FLOATING_BUTTON_LG_MIN_WIDTH_PX = 1024

export const FLOATING_BUTTON_SIZE_PX_SM = 40
export const FLOATING_BUTTON_SIZE_PX_LG = 64
export const FLOATING_BUTTON_EDGE_INSET_PX = 16

export function getFloatingButtonSizePx(viewportWidth: number): number {
  if (viewportWidth >= FLOATING_BUTTON_LG_MIN_WIDTH_PX) {
    return FLOATING_BUTTON_SIZE_PX_LG
  }
  return FLOATING_BUTTON_SIZE_PX_SM
}

export type FloatingButtonEdge = 'left' | 'right' | 'top' | 'bottom'

export interface FloatingButtonViewport {
  width: number
  height: number
}

export interface FloatingButtonEdgePosition {
  edge: FloatingButtonEdge
  /** Position along the edge as a 0..1 ratio of the perpendicular axis. */
  ratio: number
}

export function snapFloatingButtonToNearestEdge(
  position: { x: number; y: number },
  viewport: FloatingButtonViewport,
  bubbleSize: number,
): FloatingButtonEdgePosition {
  const cx = position.x + bubbleSize / 2
  const cy = position.y + bubbleSize / 2
  const distances: Record<FloatingButtonEdge, number> = {
    left: cx,
    right: viewport.width - cx,
    top: cy,
    bottom: viewport.height - cy,
  }
  const edge = (Object.keys(distances) as FloatingButtonEdge[]).reduce((nearest, candidate) =>
    distances[candidate] < distances[nearest] ? candidate : nearest,
  )
  const ratio =
    edge === 'left' || edge === 'right'
      ? clamp01(cy / viewport.height)
      : clamp01(cx / viewport.width)
  return { edge, ratio }
}

export function floatingButtonEdgeToXY(
  position: FloatingButtonEdgePosition,
  viewport: FloatingButtonViewport,
  bubbleSize: number,
): { x: number; y: number } {
  const maxX = Math.max(0, viewport.width - bubbleSize)
  const maxY = Math.max(0, viewport.height - bubbleSize)
  switch (position.edge) {
    case 'left': {
      return {
        x: FLOATING_BUTTON_EDGE_INSET_PX,
        y: clamp(position.ratio * viewport.height - bubbleSize / 2, 0, maxY),
      }
    }
    case 'right': {
      return {
        x: Math.max(0, viewport.width - bubbleSize - FLOATING_BUTTON_EDGE_INSET_PX),
        y: clamp(position.ratio * viewport.height - bubbleSize / 2, 0, maxY),
      }
    }
    case 'top': {
      return {
        x: clamp(position.ratio * viewport.width - bubbleSize / 2, 0, maxX),
        y: FLOATING_BUTTON_EDGE_INSET_PX,
      }
    }
    case 'bottom': {
      return {
        x: clamp(position.ratio * viewport.width - bubbleSize / 2, 0, maxX),
        y: Math.max(0, viewport.height - bubbleSize - FLOATING_BUTTON_EDGE_INSET_PX),
      }
    }
  }
}

function clamp01(value: number): number {
  return clamp(value, 0, 1)
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}

function storageGet(key: string): string | null {
  try {
    if (typeof window === 'undefined') {
      return null
    }
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

export function migrateLegacyLauncherKeys() {
  if (typeof window === 'undefined') {
    return
  }
  try {
    if (storageGet(AIDA_LAUNCHER_EDGE_KEY) === null) {
      const raw = storageGet(LEGACY_AIDA_LAUNCHER_EDGE_KEY)
      const parsed: unknown = raw === null ? null : JSON.parse(raw)
      if (isValidEdgePosition(parsed)) {
        writeStored(parsed)
      }
    }
    window.sessionStorage.removeItem(LEGACY_AIDA_LAUNCHER_EDGE_KEY)
    if (storageGet(AIDA_LAUNCHER_PEEK_RATIO_KEY) === null) {
      const raw = storageGet(LEGACY_AIDA_LAUNCHER_PEEK_RATIO_KEY)
      if (raw !== null) {
        const ratio = Number(raw)
        if (!Number.isNaN(ratio)) {
          writePeekRatio(Math.min(1, Math.max(0, ratio)))
        }
      }
    }
    window.sessionStorage.removeItem(LEGACY_AIDA_LAUNCHER_PEEK_RATIO_KEY)
  } catch {
    return
  }
}

function readStored(): FloatingButtonEdgePosition | null {
  try {
    const raw = storageGet(AIDA_LAUNCHER_EDGE_KEY)
    const parsed: unknown = raw === null ? null : JSON.parse(raw)
    if (!isValidEdgePosition(parsed)) {
      return null
    }
    return parsed
  } catch {
    return null
  }
}

function writeStored(position: FloatingButtonEdgePosition): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.sessionStorage.setItem(AIDA_LAUNCHER_EDGE_KEY, JSON.stringify(position))
    window.sessionStorage.removeItem(LEGACY_AIDA_LAUNCHER_EDGE_KEY)
  } catch {
    return
  }
}

function isValidEdgePosition(value: unknown): value is FloatingButtonEdgePosition {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const v = value as Record<string, unknown>
  const edge = v.edge
  const ratio = v.ratio
  if (typeof ratio !== 'number' || Number.isNaN(ratio)) {
    return false
  }
  return edge === 'left' || edge === 'right' || edge === 'top' || edge === 'bottom'
}

function getViewport(): FloatingButtonViewport {
  if (typeof window === 'undefined') {
    return { width: 1024, height: 768 }
  }
  return { width: window.innerWidth, height: window.innerHeight }
}

const DEFAULT_POSITION: FloatingButtonEdgePosition = { edge: 'right', ratio: 0.85 }

const DEFAULT_PEEK_RATIO = 0.72

export function readPeekRatio(): number {
  if (typeof window === 'undefined') {
    return DEFAULT_PEEK_RATIO
  }
  try {
    const raw = storageGet(AIDA_LAUNCHER_PEEK_RATIO_KEY)
    if (raw === null) {
      return DEFAULT_PEEK_RATIO
    }
    const ratio = Number(raw)
    if (Number.isNaN(ratio)) {
      return DEFAULT_PEEK_RATIO
    }
    return Math.min(1, Math.max(0, ratio))
  } catch {
    return DEFAULT_PEEK_RATIO
  }
}

export function writePeekRatio(ratio: number): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.sessionStorage.setItem(AIDA_LAUNCHER_PEEK_RATIO_KEY, String(ratio))
    window.sessionStorage.removeItem(LEGACY_AIDA_LAUNCHER_PEEK_RATIO_KEY)
  } catch {
    return
  }
}

/**
 * `persistDragEnd` writes to sessionStorage and returns the target without touching React state,
 * so the snap-back animation can play to completion before `commitPosition` re-syncs the motion
 * values via the effect in <AidaFloatingButton />.
 */
export function useFloatingButtonPosition() {
  const [viewport, setViewport] = React.useState<FloatingButtonViewport>(getViewport)
  const [position, setPosition] = React.useState<FloatingButtonEdgePosition>(
    () => readStored() ?? DEFAULT_POSITION,
  )

  React.useEffect(() => {
    function onResize() {
      setViewport(getViewport())
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const buttonSize = getFloatingButtonSizePx(viewport.width)

  const persistDragEnd = React.useCallback(
    (xy: { x: number; y: number }) => {
      const size = getFloatingButtonSizePx(viewport.width)
      const next = snapFloatingButtonToNearestEdge(xy, viewport, size)
      writeStored(next)
      return { position: next, target: floatingButtonEdgeToXY(next, viewport, size) }
    },
    [viewport],
  )

  const commitPosition = React.useCallback((next: FloatingButtonEdgePosition) => {
    setPosition(next)
  }, [])

  const xy = floatingButtonEdgeToXY(position, viewport, buttonSize)
  return { position, xy, viewport, buttonSize, persistDragEnd, commitPosition } as const
}
